import React from 'react';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Loader from 'src/components/ui/Loader';
import Typography from 'src/components/ui/Typography';
import Avatar from 'src/components/ui/Avatar';
import FollowUnFollowButton from 'src/components/ui/FollowUnFollowButton';
import Divider from 'src/components/ui/Divider';
import BackLink from 'src/components/ui/BackLink';

import useAppRouter from 'src/hooks/useAppRouter';
import useUser from 'src/hooks/useUser';

import { useUserProfileQuery } from 'src/hooks/__generated__/queries';
import type { Medium } from 'src/types/__generated__/graphql';

import useBreakpoints from 'src/hooks/useBreakpoints';

interface Props {
    titleTranslationKey: string;
}

const SeeAllHeaderProfile: React.FC<Props> = ({ titleTranslationKey }) => {
    const { t } = useTranslation(['common', 'profile']);
    const router = useAppRouter();
    const user = useUser();
    const userId = router.query.userId as string;
    const { previousData, data: profile = previousData } = useUserProfileQuery({
        variables: {
            id: userId,
        },
        skip: !userId,
    });

    const { isMobile } = useBreakpoints();

    return (
        <Box>
            <Box py={3} display="flex" alignItems="center" justifyContent="space-between">
                <BackLink onClick={() => router.back()}>{t('common:back')}</BackLink>
            </Box>
            <Typography variant="h1" serif>
                {t(titleTranslationKey)}
            </Typography>
            {profile?.userProfile ? (
                <Box>
                    <Box
                        display="flex"
                        flexDirection={isMobile ? 'column' : 'row'}
                        alignItems={isMobile ? 'left' : 'center'}
                        flex="1"
                    >
                        <Box my={2} ml={isMobile ? 0 : 2} mr={isMobile ? 2 : 4} display="flex" alignItems="center">
                            <Avatar
                                alt={profile.userProfile.fullName}
                                image={profile.userProfile.image as Medium}
                                size={40}
                                fallbackText={profile.userProfile.initials}
                            />
                            <Box pl={2}>
                                <Typography color="primary" weight="bold">
                                    {profile.userProfile.fullName}
                                </Typography>
                                {profile.userProfile.companyToDisplay && (
                                    <Typography variant="body2">
                                        {profile.userProfile.companyToDisplay.name}
                                        {profile.userProfile.companyToDisplay.address && (
                                            <>
                                                ,{' '}
                                                <strong>
                                                    {profile.userProfile.companyToDisplay.address.city?.name ??
                                                        profile.userProfile.companyToDisplay.address.cityName}
                                                </strong>
                                            </>
                                        )}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        {profile.userProfile.id !== user?.id && <FollowUnFollowButton user={profile.userProfile} />}
                    </Box>
                    <Box mt={isMobile && profile.userProfile.id !== user?.id ? 2 : 0}>
                        <Divider />
                    </Box>
                </Box>
            ) : (
                <Loader />
            )}
        </Box>
    );
};

export default SeeAllHeaderProfile;
